import React from 'react';
import { Helmet } from 'react-helmet-async';
import { getSiteConfig, getSiteUrl } from '../../utils/siteConfig';

export const OrganizationSchema: React.FC = () => {
  const siteConfig = getSiteConfig();
  const siteUrl = getSiteUrl();
  const defaultLogoUrl = `${siteUrl}/assets/images/OGimage/og-coolandcare.png`;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Cool & Care',
    url: siteUrl,
    logo: defaultLogoUrl,
    description: 'IT, Security, HVAC, Solar Solutions and Software Services in DRC',
    address: {
      '@type': 'PostalAddress',
      addressCountry: siteConfig.structuredData.addressCountry,
      addressLocality: siteConfig.structuredData.addressLocality,
      streetAddress: `${siteConfig.addressLines[0]}, ${siteConfig.addressLines[1]}`,
    },
    sameAs: [],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['English', 'French', 'Chinese'],
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

export const LocalBusinessSchema: React.FC = () => {
  const siteConfig = getSiteConfig();
  const siteUrl = getSiteUrl();
  const defaultLogoUrl = `${siteUrl}/assets/images/OGimage/og-coolandcare.png`;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Cool & Care',
    url: siteUrl,
    image: defaultLogoUrl,
    description: siteConfig.structuredData.description,
    address: {
      '@type': 'PostalAddress',
      addressCountry: siteConfig.structuredData.addressCountry,
      addressLocality: siteConfig.structuredData.addressLocality,
      streetAddress: `${siteConfig.addressLines[0]}, ${siteConfig.addressLines[1]}`,
    },
    ...(siteConfig.structuredData.geo && {
      geo: {
        '@type': 'GeoCoordinates',
        latitude: siteConfig.structuredData.geo.latitude,
        longitude: siteConfig.structuredData.geo.longitude,
      },
    }),
    areaServed: {
      '@type': 'Country',
      name: siteConfig.structuredData.areaServed,
    },
    priceRange: '$$',
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '17:00',
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

interface ServiceSchemaProps {
  name: string;
  description: string;
  url: string;
}

export const ServiceSchema: React.FC<ServiceSchemaProps> = ({ name, description, url }) => {
  const siteConfig = getSiteConfig();
  const siteUrl = getSiteUrl();

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    url: `${siteUrl}${url}`,
    provider: {
      '@type': 'Organization',
      name: 'Cool & Care',
      url: siteUrl,
    },
    areaServed: {
      '@type': 'Country',
      name: siteConfig.structuredData.areaServed,
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

interface ArticleSchemaProps {
  title: string;
  description: string;
  url: string;
  image?: string;
  publishedAt?: string;
  author?: string;
}

export const ArticleSchema: React.FC<ArticleSchemaProps> = ({
  title,
  description,
  url,
  image,
  publishedAt,
  author,
}) => {
  const siteUrl = getSiteUrl();
  const defaultLogoUrl = `${siteUrl}/assets/images/OGimage/og-coolandcare.png`;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    url: `${siteUrl}${url}`,
    image: image || `${siteUrl}/assets/images/og-default.jpg`,
    datePublished: publishedAt,
    author: {
      '@type': 'Person',
      name: author || 'Cool & Care Team',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Cool & Care',
      logo: {
        '@type': 'ImageObject',
        url: defaultLogoUrl,
      },
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

export const WebSiteSchema: React.FC = () => {
  const siteUrl = getSiteUrl();

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Cool & Care',
    url: siteUrl,
    inLanguage: ['en', 'fr', 'zh'],
    publisher: {
      '@type': 'Organization',
      name: 'Cool & Care',
      url: siteUrl,
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

interface BreadcrumbItem {
  name: string;
  url: string;
}

export const BreadcrumbSchema: React.FC<{ items: BreadcrumbItem[] }> = ({ items }) => {
  const siteUrl = getSiteUrl();

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      ...items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 2,
        name: item.name,
        item: `${siteUrl}${item.url}`,
      })),
    ],
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};
